import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CategoryList = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const baseUrl = process.env.REACT_APP_API_URL;
  const url = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

  // Fetch categories from backend (server-side search)
  const fetchData = useCallback(async (search = "") => {
    try {
      setLoading(true);

      const headers = {
        "x-access-token": localStorage.getItem("jwttoken"),
      };

      const response = await axios.get(`${url}postcategory/showAll`, {
        headers,
        params: {
          search: search,
          page: page,
          limit: 10
        }
      });

      setCategoryList(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setLoading(false);
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.err || "Failed to fetch categories";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 3000,
        theme: "colored"
      });
      setLoading(false);
    }
  }, [url, page]);

  // Fetch on initial load + whenever search changes
  useEffect(() => {
    fetchData(searchTerm);
  }, [fetchData,searchTerm]);

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const headers = {
        "x-access-token": localStorage.getItem("jwttoken"),
      };

      await axios.delete(
        `${url}postcategory/delete/${categoryToDelete.srno}`,
        { headers }
      );
      toast.success("Category deleted successfully", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
      });

      if (categoryList.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchData(searchTerm);
      }
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Delete Error:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.err || "Error deleting category";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        theme: "colored"
      });
      setShowDeleteModal(false);
  };
};

  return (
    <div>
      <ToastContainer />
      <div className="main-card">

        {/* Toolbar */}
        <div className="toolbar-container">
          <h2 className="page-title">Category Management</h2>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span className="results-count">
              {categoryList.length} results
            </span>

            <input
              type="text"
              placeholder="Search by Category Name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(1);
              }}
              className="search-box"
            />

            <Link to="/addueserid" className="btn-add-primary">
              + Add Category
            </Link>
          </div>
        </div>

        {/* Header Panel */}
        <div className="table-header-panel">
          Category List
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table className="custom-table">
            <thead>
              <tr>
                <th>SR No.</th>
                <th>ID</th>
                <th>Category Name</th>
                <th>Display Name</th>
                <th>Details</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : categoryList.length === 0 ? (
                <tr>
                  <td colSpan="7">
                    <div className="empty-state">
                      <div className="empty-state-icon">📂</div>
                      <div className="empty-state-text">
                        No categories found
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                categoryList.map((item, index) => (
                  <tr key={item.srno}>
                    <td>{(page - 1) * 10 + index + 1}</td>
                    <td>{item.categoryid}</td>
                    <td style={{ fontWeight: "600" }}>
                      {item.categoryname}
                    </td>
                    <td>{item.categorydisplayname}</td>
                    <td>{item.categorydetails}</td>
                     <td>
                      <span className={item.isactive ? "badge-active" : "badge-inactive"}>
                        {item.isactive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="action-btn-container">
                        <Link
                          to={`/categoryupdate/${item.srno}`}
                          className="btn-action-edit"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => confirmDelete(item)}
                          className="btn-action-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button
            className="btn-pagination"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{ marginRight: "10px" }}
          >
            Prev
          </button>

          <select
            value={page}
            onChange={(e) => setPage(Number(e.target.value))}
            className="page-selector"
            style={{ margin: "0 10px", padding: "5px" }}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Page {i + 1}
              </option>
            ))}
          </select>

          <span> of {totalPages} </span>

          <button
            className="btn-pagination"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
            style={{ marginLeft: "10px" }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the category: <strong>{categoryToDelete?.categoryname}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCategory}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};


export default CategoryList;